import {TradingAlertService} from "../services/TradingAlertService";
import {Observable, ReplaySubject, Scheduler} from "rxjs";
import {sortByCodeAsc, sortByMatchedDesc, TradingAlert} from "../models/alert/TradingAlert";
import {map} from "rxjs/operators";
import {TradingAlertStatus} from "../models/alert/TradingAlertStatus";
import {TradingAlertDialogService} from "../services/TradingAlertDialogService";
import {Transform} from "../../types/GenericTypes";
import {ArrayUtil} from "../../js/util/ArrayUtil";
import {SystemEventService} from "../../services/system-state/SystemEventService";
import {SystemEvent} from "../../services/system-state/SystemEvent";
import {CurrentUserService} from "../../services/user/CurrentUserService";
import {identity} from "../../js/util/UtilFunctions";

export class TradingAlertViewStore {

    private readonly current$ = new ReplaySubject<Array<TradingAlert>>(1);
    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    public constructor(private readonly alertService: TradingAlertService,
                       private readonly dialogService: TradingAlertDialogService,
                       private readonly systemEventService: SystemEventService,
                       private readonly currentUserService: CurrentUserService) {
        this.init();
    }

    private init() {
        const timer = Observable.timer(0, 60000);
        const userNotIdle = this.systemEventService
                                .idle()
                                .map((evt) => evt === SystemEvent.IDLE_END)
                                .distinctUntilChanged();
        const userLoggedIn = this.currentUserService
                                 .current
                                 .map((u) => u.isLoggedIn)
                                 .distinctUntilChanged();

        const shouldPoll = Observable.combineLatest(userNotIdle, userLoggedIn)
                                     .map(([active, loggedIn]) => active && loggedIn);

        timer.takeUntil(this.destroyed$)
             .withLatestFrom(shouldPoll)
             .map(([_, poll]) => poll)
             .filter((poll) => poll)
             .subscribe(() => this.reload());
    }

    private reload() {
        const response = this.alertService.alerts();
        Observable.fromPromise(response)
                  .subscribe((alerts) => this.current$.next(alerts));
    }

    public get alerts(): Observable<Array<TradingAlert>> {
        return this.current$
                   .observeOn(Scheduler.async)
                   .map((alerts) => ArrayUtil.clone(alerts));
    }

    public activeAlerts(): Observable<Array<TradingAlert>> {
        return this.current$.pipe(map(this.filterForStatus(TradingAlertStatus.ACTIVE)))
                   .pipe(map((a) => a.sort(sortByCodeAsc())))
                   .observeOn(Scheduler.async);
    }

    public matchedAlerts(): Observable<Array<TradingAlert>> {
        return this.current$.pipe(map(this.filterForStatus(TradingAlertStatus.MATCHED)))
                   .pipe(map((a) => a.sort(sortByMatchedDesc())))
                   .observeOn(Scheduler.async);
    }

    private filterForStatus(status: TradingAlertStatus): Transform<Array<TradingAlert>, Array<TradingAlert>> {
        return (alerts) => alerts.filter((a) => a.status === status);
    }

    public addAlert() {
        Observable.fromPromise(this.dialogService.open())
                  .flatMap((newAlert) => this.alertService.createTradingAlert(newAlert))
                  .subscribe(() => this.reload());
    }

    public cancelAlert(id: number) {
        const response = this.alertService.cancelTradingAlert(id);
        Observable.fromPromise(response)
                  .subscribe(() => this.reload());
    }

    public acknowledgeAlert(id: number) {
        const response = this.alertService.acknowledgeTradingAlert(id);
        Observable.fromPromise(response)
                  .subscribe(() => this.reload());
    }

    public editAlert(alert: TradingAlert) {
        Observable.fromPromise(this.dialogService.open(alert))
                  .flatMap((updated) => this.alertService.update(alert.id, updated))
                  .subscribe(() => this.reload());
    }

    public destroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
